import { QueryClient, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ENDPOINTS } from 'config/endpoints';
import CronoError from 'crono-fe-common/types/error';
import { Request } from 'crono-fe-common/types/request';
import { Response } from 'crono-fe-common/types/response';
import client from 'utils/clients/client';
import {
  TimelineGetDto,
  TimelineItemGetDto,
  TimelineItemType,
  TimelineReplyType,
  TimelineSearch,
  TimelineSectionKey,
} from './timeline.types';

export const TIMELINE_FUTURE_INITIAL_LIMIT = 10;
export const TIMELINE_PAST_INITIAL_LIMIT = 10;
export const TIMELINE_EXPAND_LIMIT = 10;

const ENDPOINT = ENDPOINTS.timeline.search;

export interface TimelineFilters {
  accountId: string;
  prospectId?: string | null;
  opportunityId?: string | null;
  noUser?: boolean | null;
  userIds?: number[] | null;
  userListIds?: number[] | null;
  prospectIds?: string[] | null;
  types?: TimelineItemType[] | null;
  replyTypes?: TimelineReplyType[] | null;
  from?: Date | null;
  to?: Date | null;
}

// accountId and section are dedicated leading key segments (ahead of the
// stringified filters) so cached queries can be invalidated by company, and
// optionally by a single section, with a plain prefix match. Each segment is
// only appended when the previous ones are present, so the same helper builds
// both full query keys and the invalidation prefixes. `variant` separates
// queries that share filters but not their shape (paginated vs single item).
const timelineQueryKey = (
  accountId?: string,
  section?: TimelineSectionKey,
  filters?: TimelineFilters,
  variant?: string,
) => {
  const key: unknown[] = [ENDPOINT];

  if (accountId == null) {
    return key;
  }
  key.push(accountId);

  if (section == null) {
    return key;
  }
  key.push(section);

  if (filters == null) {
    return key;
  }
  key.push(JSON.stringify(filters));

  if (variant == null) {
    return key;
  }
  key.push(variant);

  return key;
};

export const buildSearch = (
  filters: TimelineFilters,
  section: TimelineSectionKey,
  limit: number,
  offset: number,
): TimelineSearch => ({
  accountId: filters.accountId,
  prospectId: filters.prospectId ?? null,
  opportunityId: filters.opportunityId ?? null,
  noUser: filters.noUser ?? null,
  userIds: filters.userIds ?? null,
  userListIds: filters.userListIds ?? null,
  prospectIds: filters.prospectIds ?? null,
  types: filters.types ?? null,
  replyTypes: filters.replyTypes ?? null,
  since: filters?.from ? filters.from.toISOString() : null,
  to: filters?.to ? filters.to.toISOString() : null,
  future: section === 'future' ? { limit, offset } : null,
  past: section === 'past' ? { limit, offset } : null,
});

const fetchTimeline = (
  search: TimelineSearch,
): Promise<Response<TimelineGetDto>> => {
  const request: Request = {
    url: ENDPOINT,
    config: {
      method: 'post',
      data: { data: search },
    },
  };
  return client(request);
};

function useTimelineSectionInfinite(
  filters: TimelineFilters,
  section: TimelineSectionKey,
  initialLimit: number,
  enabled = true,
) {
  return useInfiniteQuery<Response<TimelineGetDto>, CronoError>({
    queryKey: timelineQueryKey(filters.accountId, section, filters),
    queryFn: ({ pageParam = 0 }) => {
      const offset: number = pageParam;
      const limit = offset === 0 ? initialLimit : TIMELINE_EXPAND_LIMIT;
      return fetchTimeline(buildSearch(filters, section, limit, offset));
    },
    getNextPageParam: (lastPage) =>
      lastPage.data.data?.[section]?.next || undefined,
    enabled: enabled && !!filters.accountId,
    refetchOnWindowFocus: false,
  });
}

export function useTimelineFutureInfinite(
  filters: TimelineFilters,
  enabled = true,
) {
  return useTimelineSectionInfinite(
    filters,
    'future',
    TIMELINE_FUTURE_INITIAL_LIMIT,
    enabled,
  );
}

export function useTimelinePastInfinite(
  filters: TimelineFilters,
  enabled = true,
) {
  return useTimelineSectionInfinite(
    filters,
    'past',
    TIMELINE_PAST_INITIAL_LIMIT,
    enabled,
  );
}

/**
 * The nearest upcoming item matching `filters`, fetched on its own so it can be
 * found beyond the pages the paginated future query has loaded.
 */
export function useTimelineFirstFutureItem(
  filters: TimelineFilters,
  enabled = true,
) {
  return useQuery<
    Response<TimelineGetDto>,
    CronoError,
    TimelineItemGetDto | null
  >({
    queryKey: timelineQueryKey(filters.accountId, 'future', filters, 'first'),
    queryFn: () => fetchTimeline(buildSearch(filters, 'future', 1, 0)),
    select: (response) => response.data.data?.future?.data[0] ?? null,
    enabled: enabled && !!filters.accountId,
    refetchOnWindowFocus: false,
  });
}

export const invalidateTimelineQueries = (
  queryClient: QueryClient,
  accountId?: string,
  section?: TimelineSectionKey,
) =>
  queryClient.invalidateQueries({
    queryKey: timelineQueryKey(accountId, section),
  });
